import { useEffect, useState } from 'react'
import type React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@platform/ui'
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react'
import { useTranslations } from '@/i18n/useTranslations'
import { BrandLogo } from '@/modules/shared/BrandLogo'
import { useRouter, useSearchParams } from '@/lib/mocks/router'
import { completeRecoveryKyc } from '@/lib/account-security/api'

type State = 'verifying' | 'done' | 'error'

/**
 * Pantalla de retorno del re-KYC de recuperación. DIDIT redirige aquí con ?id=<requestId>;
 * al montar cerramos el ticket (POST /recovery/:id/kyc-complete) y queda pendiente de
 * aprobación por el admin en BigAdmin.
 */
export function RecoveryVerify() {
  const t = useTranslations('Recovery')
  const router = useRouter()
  const params = useSearchParams()
  const id = params.get('id')
  const [state, setState] = useState<State>('verifying')

  useEffect(() => {
    let cancelled = false
    if (!id) {
      setState('error')
      return
    }
    completeRecoveryKyc(id)
      .then(() => {
        if (!cancelled) setState('done')
      })
      .catch(() => {
        if (!cancelled) setState('error')
      })
    return () => {
      cancelled = true
    }
  }, [id])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm space-y-6 text-center">
        <div className="flex justify-center">
          <BrandLogo variant="primary" textClassName="text-2xl font-bold tracking-tight text-foreground" />
        </div>

        {state === 'verifying' && (
          <Header icon={<Loader2 className="h-8 w-8 animate-spin" />} title={t('verify_title')} subtitle={t('verify_subtitle')} />
        )}
        {state === 'done' && (
          <>
            <Header icon={<CheckCircle2 className="h-8 w-8" />} title={t('verify_done_title')} subtitle={t('verify_done_subtitle')} tone="success" />
            <p className="text-sm text-muted-foreground">{t('verify_done_desc')}</p>
            <Button onClick={() => router.push('/')} className="w-full h-11 font-semibold">
              {t('done_cta')}
            </Button>
          </>
        )}
        {state === 'error' && (
          <>
            <Header icon={<ShieldAlert className="h-8 w-8" />} title={t('verify_error_title')} subtitle={t('verify_error_subtitle')} tone="error" />
            <Button onClick={() => router.push('/')} variant="outline" className="w-full h-11 font-semibold">
              {t('back_to_login')}
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

function Header({
  icon,
  title,
  subtitle,
  tone = 'primary',
}: {
  icon: React.ReactNode
  title: string
  subtitle: string
  tone?: 'primary' | 'success' | 'error'
}) {
  const ring =
    tone === 'success'
      ? 'bg-emerald-500/15 text-emerald-600'
      : tone === 'error'
        ? 'bg-destructive/15 text-destructive'
        : 'bg-primary/15 text-primary'
  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <div className={`flex h-16 w-16 items-center justify-center rounded-full ${ring}`}>{icon}</div>
      </div>
      <div className="space-y-1">
        <h2 className="text-2xl font-bold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  )
}

export const Route = createFileRoute('/recovery/verify')({ component: RecoveryVerify })
